import React, { useState, useEffect } from "react";
import {
  AiOutlineShoppingCart,
  AiOutlineHeart,
  AiFillHeart,
  AiOutlineStar,
  AiFillStar,
} from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import { addToWishlist, deleteFromWishlist } from "../actions/wishlistActions";
import { addToCart } from "../actions/cartActions";
import { useImage } from "../contexts/ImageContext";
import { useNavigate } from "react-router-dom";
import Notification from "./Notification";
import "../styles/new.css";
import formatMoney from "./formatMoney";
import { isInWishlist, getWishlistItem } from "../utils/globals";
import { fetchWishlist } from "../actions/wishlistActions";

const ProductCard = ({ product, preferredTag }) => {
  const {
    product_id,
    slug,
    name,
    brand,
    base_price,
    discounted_price,
    product_rating,
    image,
    tags,
    oldPrice,
  } = product;
  const { getImageUrl } = useImage();
  const imageUrl = getImageUrl(image);
  const { isAuthenticated } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { results = [] } = useSelector((state) => state.wishlist);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationAction, setNotificationAction] = useState("");

  useEffect(() => {
    localStorage.setItem("wishlist", JSON.stringify(results));
  }, [results]);

  const handleAddToCart = (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (!isAuthenticated) {
      navigate(
        `/signin?redirect=${encodeURIComponent(window.location.pathname)}`
      );
      return;
    }

    dispatch(addToCart({ product_id, quantity: 1 }));
    setNotificationMessage(`${name} added to cart successfully!`);
    setNotificationAction("View Cart");
    setShowNotification(true);
  };

  const handleWishlistToggle = async (e) => {
    e.stopPropagation();
    e.preventDefault();

    if (!isAuthenticated) {
      navigate("/signin");
      return;
    }

    try {
      let result;
      if (isInWishlist(product_id)) {
        result = await dispatch(
          deleteFromWishlist(getWishlistItem(product_id).wishlist_id)
        );
        setNotificationMessage(`${name} removed from wishlist`);
      } else {
        result = await dispatch(addToWishlist(product_id));
        if (result.isAlreadyInWishlist) {
          setNotificationMessage(`Item is already in wishlist`);
        } else {
          setNotificationMessage(`${name} added to wishlist`);
        }
      }

      setNotificationAction("View Wishlist");
      setShowNotification(true);
      dispatch(fetchWishlist());
    } catch (error) {
      console.error("Error toggling wishlist:", error);

      setNotificationMessage("Failed to update wishlist");
      setNotificationAction("Try Again");
      setShowNotification(true);
    }
  };

  const handleProductView = (e) => {
    e.stopPropagation();
    e.preventDefault();
    navigate(`/product/${slug || product_id}`);
  };

  // Determine which tag to display
  const getDisplayTag = () => {
    if (!tags || tags.length === 0) return null;

    // If a preferred tag is specified (from parent component) and product has it, show it
    if (preferredTag && tags.includes(preferredTag)) {
      return preferredTag;
    }

    // Otherwise show the first tag as before
    return tags[0];
  };

  const displayTag = getDisplayTag();
  const isHotDeal = tags.includes("HOT DEAL");

  return (
    <div onClick={handleProductView}>
      <div className="new-product-card">
        <div
          className="new-product-image"
          style={{ backgroundImage: `url(${imageUrl})` }}
        >
          {displayTag && (
            <span
              className={`new-product-tag ${
                displayTag.toLowerCase() === "hot deal" ? "hot-deal" : ""
              }`}
            >
              {displayTag}
            </span>
          )}
          <div className="new-product-icons-overlay">
            <button
              className={`icon-button ${
                tags && tags[0]?.toLowerCase() === "hot deal" ? "hot-deal" : ""
              }`}
              onClick={handleWishlistToggle}
            >
              {isInWishlist(product_id) ? <AiFillHeart /> : <AiOutlineHeart />}
            </button>
          </div>
        </div>
        <div className="new-product-details">
          <div className="new-product-content">
            <div className="new-product-name-container">
              <h3
                className="new-product-brand"
                dangerouslySetInnerHTML={{ __html: brand }}
              ></h3>
              <div className="new-product-rating">
                <span className="new-product-rating">
                  {[...Array(1)].map((_, index) => (
                    <span key={index}>
                      <AiFillStar className="filled-star" />
                    </span>
                  ))}
                  <span className="review-count">
                    ({product.reviews?.length || 0})
                  </span>
                </span>
              </div>
            </div>
            <p
              className="new-product-name"
              dangerouslySetInnerHTML={{ __html: name }}
            ></p>
          </div>
          <div className="new-product-bottom-section">
            <div className="new-product-price-container">
              {oldPrice && (
                <p className="new-product-old-price">{formatMoney(oldPrice)}</p>
              )}
              {discounted_price === "0.00" || discounted_price === 0 ? (
                <p className="new-product-price">{formatMoney(base_price)}</p>
              ) : discounted_price === base_price ? (
                <p className="new-product-price">
                  {formatMoney(discounted_price)}
                </p>
              ) : (
                <>
                  <p className="new-product-old-price">
                    {formatMoney(base_price)}
                  </p>
                  <p className="new-product-price">
                    {formatMoney(discounted_price)}
                  </p>
                </>
              )}
            </div>
            <button
              onClick={handleAddToCart}
              className={`new-product-add-to-cart-btn`}
            >
              Add to Cart
            </button>
          </div>
        </div>
      </div>
      <Notification
        message={notificationMessage}
        isVisible={showNotification}
        onClose={() => setShowNotification(false)}
        actionText={notificationAction}
        onActionClick={() =>
          navigate(
            notificationAction.toLowerCase().includes("cart")
              ? "/cart"
              : "/wishlist"
          )
        }
      />
    </div>
  );
};

export default ProductCard;
